import { supabase } from '@/lib/supabase';

export type SignupCouponBenefitType = 'free_months' | 'discount';
export type SignupPlanCode = 'ignite' | 'accelerate' | 'command' | 'business';

export interface SignupCoupon {
  id: string;
  code: string;
  plan_code: SignupPlanCode;
  benefit_type: SignupCouponBenefitType;
  free_months: number | null;
  discount_percent: number | null;
  discount_months: number | null;
  max_uses: number | null;
  used_count: number;
  expires_at: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
}

export type UpsertSignupCouponPayload = {
  code: string;
  plan_code: SignupPlanCode;
  benefit_type: SignupCouponBenefitType;
  free_months?: number | null;
  discount_percent?: number | null;
  discount_months?: number | null;
  max_uses?: number | null;
  expires_at?: string | null;
  active?: boolean;
  notes?: string | null;
};

const PLAN_LABELS: Record<SignupPlanCode, string> = {
  ignite: 'Ignite',
  accelerate: 'Accelerate',
  command: 'Command',
  business: 'Business',
};

export function describeSignupCouponBenefit(c: SignupCoupon): string {
  const plan = PLAN_LABELS[c.plan_code] ?? c.plan_code;
  if (c.benefit_type === 'free_months' && c.free_months) {
    return `${c.free_months} mo free · ${plan}`;
  }
  if (c.benefit_type === 'discount' && c.discount_percent && c.discount_months) {
    return `${c.discount_percent}% off × ${c.discount_months} mo · ${plan}`;
  }
  return plan;
}

export const signupCouponsApi = {
  list: async (): Promise<SignupCoupon[]> => {
    const { data, error } = await supabase
      .from('signup_coupons')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as SignupCoupon[];
  },

  create: async (payload: UpsertSignupCouponPayload): Promise<SignupCoupon> => {
    const row = {
      code: payload.code.trim().toUpperCase(),
      plan_code: payload.plan_code,
      benefit_type: payload.benefit_type,
      free_months: payload.benefit_type === 'free_months' ? payload.free_months ?? null : null,
      discount_percent: payload.benefit_type === 'discount' ? payload.discount_percent ?? null : null,
      discount_months: payload.benefit_type === 'discount' ? payload.discount_months ?? null : null,
      max_uses: payload.max_uses ?? null,
      expires_at: payload.expires_at || null,
      active: payload.active ?? true,
      notes: payload.notes?.trim() || null,
    };

    const { data, error } = await supabase.from('signup_coupons').insert(row).select('*').single();
    if (error) throw error;
    return data as SignupCoupon;
  },

  update: async (id: string, payload: Partial<UpsertSignupCouponPayload>): Promise<SignupCoupon> => {
    const updates: Record<string, unknown> = {};
    if (payload.code !== undefined) updates.code = payload.code.trim().toUpperCase();
    if (payload.plan_code !== undefined) updates.plan_code = payload.plan_code;
    if (payload.benefit_type !== undefined) updates.benefit_type = payload.benefit_type;
    if (payload.free_months !== undefined) updates.free_months = payload.free_months;
    if (payload.discount_percent !== undefined) updates.discount_percent = payload.discount_percent;
    if (payload.discount_months !== undefined) updates.discount_months = payload.discount_months;
    if (payload.max_uses !== undefined) updates.max_uses = payload.max_uses;
    if (payload.expires_at !== undefined) updates.expires_at = payload.expires_at || null;
    if (payload.active !== undefined) updates.active = payload.active;
    if (payload.notes !== undefined) updates.notes = payload.notes?.trim() || null;

    const { data, error } = await supabase
      .from('signup_coupons')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    return data as SignupCoupon;
  },

  setActive: async (id: string, active: boolean): Promise<void> => {
    const { error } = await supabase.from('signup_coupons').update({ active }).eq('id', id);
    if (error) throw error;
  },
};
